import {
  BoxGeometry,
  BufferGeometry,
  CatmullRomCurve3,
  ExtrudeGeometry,
  MeshBasicMaterial,
  Path,
  Shape,
  TubeGeometry,
  Vector2,
  Vector3,
} from 'three';
import { mergeBufferGeometries } from 'three-stdlib';

import { assertExhaustiveSwitch } from '@paragon/utilities';

import { SnapType } from '../dimension/SnapVisual';

// double circle
const angle = (Math.PI * 2) / 10;
const points1 = [
  new Vector3(Math.cos(0) * (1 / 2), Math.sin(0) * (1 / 2), 2),
  new Vector3(+(Math.cos(angle) * (1 / 2)), +(Math.sin(angle) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 2) * (1 / 2)), +(Math.sin(angle * 2) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 3) * (1 / 2)), +(Math.sin(angle * 3) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 4) * (1 / 2)), +(Math.sin(angle * 4) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 5) * (1 / 2)), +(Math.sin(angle * 5) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 6) * (1 / 2)), +(Math.sin(angle * 6) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 7) * (1 / 2)), +(Math.sin(angle * 7) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 8) * (1 / 2)), +(Math.sin(angle * 8) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 9) * (1 / 2)), +(Math.sin(angle * 9) * (1 / 2)), 2),
  new Vector3(+(Math.cos(angle * 10.1) * (1 / 2)), +(Math.sin(angle * 10.1) * (1 / 2)), 2),
];
const path1 = new CatmullRomCurve3(points1);
const points2 = [
  new Vector3(Math.cos(0) * (1 / 4), Math.sin(0) * (1 / 4), 2),
  new Vector3(+(Math.cos(angle) * (1 / 4)), +(Math.sin(angle) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 2) * (1 / 4)), +(Math.sin(angle * 2) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 3) * (1 / 4)), +(Math.sin(angle * 3) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 4) * (1 / 4)), +(Math.sin(angle * 4) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 5) * (1 / 4)), +(Math.sin(angle * 5) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 6) * (1 / 4)), +(Math.sin(angle * 6) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 7) * (1 / 4)), +(Math.sin(angle * 7) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 8) * (1 / 4)), +(Math.sin(angle * 8) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 9) * (1 / 4)), +(Math.sin(angle * 9) * (1 / 4)), 2),
  new Vector3(+(Math.cos(angle * 10.1) * (1 / 4)), +(Math.sin(angle * 10.1) * (1 / 4)), 2),
];
const path2 = new CatmullRomCurve3(points2);
const centroidGeometryOuter = new TubeGeometry(path1, 20, 1 / 13, 5, false).center();
const centroidGeometryInner = new TubeGeometry(path2, 20, 1 / 13, 5, false).center();

// cross
const crossGeometryVertical = new BoxGeometry(0.1, 1, 0.1).center();
const crossGeometryHorizontal = new BoxGeometry(1, 0.1, 0.1).center();

// triangle
const hSize = 1 / 2;
const top = new Vector2(0, hSize);
const baseLeft = new Vector2(-hSize, -hSize);
const baseRight = new Vector2(hSize, -hSize);
const shape = new Shape([top, baseLeft, baseRight]);
const topHole = new Vector2(0, hSize / 2);
const baseLeftHole = new Vector2(-hSize / 2, -hSize / 1.5);
const baseRightHole = new Vector2(hSize / 2, -hSize / 1.5);
shape.holes.push(new Path([topHole, baseLeftHole, baseRightHole]));
const triangleExtrudeOptions = { bevelEnabled: false, depth: 1 };
const triangleGeometry = new ExtrudeGeometry(shape, triangleExtrudeOptions).center();

// square with hole
const length = 1 / 2;
const bottomLeft = new Vector2(-length, -length);
const topLeft = new Vector2(-length, length);
const topRight = new Vector2(length, length);
const bottomRight = new Vector2(length, -length);
const boxShape = new Shape([bottomLeft, topLeft, topRight, bottomRight]);
const bottomLeftHole = new Vector2(-length / 2, -length / 2);
const topLeftHole = new Vector2(-length / 2, length / 2);
const topRightHole = new Vector2(length / 2, length / 2);
const bottomRightHole = new Vector2(length / 2, -length / 2);
boxShape.holes.push(new Path([bottomLeftHole, bottomRightHole, topRightHole, topLeftHole]));
const squareExtrudeOptions = { bevelEnabled: false, depth: 1 };
const squareGeometry = new ExtrudeGeometry(boxShape, squareExtrudeOptions).center();

const centroidGeometry = mergeBufferGeometries([centroidGeometryOuter, centroidGeometryInner])!;
const intersectionGeometry = mergeBufferGeometries([
  centroidGeometryOuter,
  crossGeometryVertical,
  crossGeometryHorizontal,
])!;

export function getSnapGeometry(snapType: SnapType): BufferGeometry {
  switch (snapType) {
    case 'Midline':
      return triangleGeometry;
    case 'Centroid':
      return centroidGeometry;
    case 'Intersection':
      return intersectionGeometry;
    case 'Vertex':
      return squareGeometry;
    default:
      assertExhaustiveSwitch(snapType, 'Unrecognized snap type.');
  }
}

const midlineColor = 0xffff00;
const centroidColor = 0xffffff;
const intersectionColor = 0xe20202;
const vertexColor = 0x40d600;
const selectedColor = 0xff0000;

const midlineMaterial = new MeshBasicMaterial({ transparent: true, color: midlineColor, depthTest: false });
const intersectionMaterial = new MeshBasicMaterial({ transparent: true, color: intersectionColor, depthTest: false });
const centroidMaterial = new MeshBasicMaterial({ transparent: true, color: centroidColor, depthTest: false });
const vertexMaterial = new MeshBasicMaterial({ transparent: true, color: vertexColor, depthTest: false });
const selectedMaterial = new MeshBasicMaterial({ transparent: true, color: selectedColor, depthTest: false });

export function getSnapMaterial(snapType: SnapType, isSelected: boolean) {
  if (isSelected) return selectedMaterial;

  switch (snapType) {
    case 'Midline':
      return midlineMaterial;
    case 'Centroid':
      return centroidMaterial;
    case 'Intersection':
      return intersectionMaterial;
    case 'Vertex':
      return vertexMaterial;
    default:
      assertExhaustiveSwitch(snapType, 'Unrecognized snap type.');
  }
}
